import { formatISO, isBefore, isValid, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';

/** The local storage key used to store when user has last agreed to terms of use */
const AGREED_TO_TERMS_OF_USE = 'AGREED_TO_MVWS_TERMS_OF_USE';

/**
 * A custom hook that manages the user's agreement to service terms using local storage.
 *
 * @returns An object containing:
 * - `hasAgreed` (boolean): Indicates whether the user has agreed to the service terms.
 * - `agreedDate` (Date | null): The date (and time) of when the user agreed to the terms, or `null`
 *   if not agreed.
 * - `agreeToTerms` (function): A function to record the user's agreement to the terms by
 *   storing the current date (and time) in ISO 8601 format into browser's local storage.
 * - `isReagreementNeeded` (function): A function that takes a `Date` parameter and returns
 *   `true` if the user needs to re-agree to the terms (i.e., if they have not agreed or if
 *   their agreement date is earlier than the provided date), `false` otherwise.
 */
export function useHasAgreedToServiceTerms() {
    const [agreedDate, setAgreedDate] = useLocalStorage<Date | null>(
        AGREED_TO_TERMS_OF_USE,
        null,
        {
            // serialize Date to ISO 8601 string or 'null' for null value
            serializer: (value) => {
                return value === null ? 'null' : formatISO(value);
            },

            // deserialize ISO 8601 string to Date or null for invalid/missing date
            deserializer: (value) => {
                const result = parseISO(value);
                if (isValid(result)) {
                    return result;
                }

                return null;
            },
        }
    );

    /**
     * Records the user's agreement to the service terms by storing current date.
     * Date is stored into local storage in ISO 8601 format.
     */
    const agreeToTerms = () => {
        setAgreedDate(new Date());
    };

    /**
     * Indicates whether the user has agreed to the service terms.
     * This is determined by checking if there is a valid date
     * indicating the agreement. Does NOT guarantee that the agreement
     * is up-to-date -- use `isReagreementNeeded` to check that.
     *
     * @returns `true` if the user has agreed to the service terms, `false` otherwise.
     */
    const hasAgreed = useMemo(() => isValid(agreedDate), [agreedDate]);

    /**
     * Determines whether the user needs to re-agree to the service terms based on a given date.
     * For corner-case purposes the test is inclusive, meaning if the agreed date is on the same
     * second as the given date, re-agreement is not needed.
     *
     * @param date - The date to compare against the agreed date.
     * @returns `true` if the user has not agreed to the terms or if the agreed date is earlier
     * than the given date, `false` otherwise.
     */
    const isReagreementNeeded = (date: Date): boolean => {
        if (!agreedDate) {
            return true;
        }

        return isBefore(agreedDate, date);
    };

    return {
        hasAgreed,
        agreedDate,
        agreeToTerms,
        isReagreementNeeded,
    };
}
